const DEFAULT_CONFIG = {
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000,
};

const getToken = () => {
  return localStorage.getItem("token");
};

const clearAuth = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  localStorage.removeItem("cart");
  localStorage.removeItem("cartDetailItems");
};

const getAuthHeaders = () => {
  const token = getToken();
  return {
    ...DEFAULT_CONFIG.headers,
    ...(token ? { token: `${token}` } : {}),
  };
};

const handleUnauthorized = () => {
  clearAuth();
  if (window.location.pathname !== "/") {
    window.location.href = "/";
  }
  return {
    status: false,
    message: "Unauthorized",
  };
};

const parseJsonSafely = async (response) => {
  try {
    console.log('Response bodyUsed:', response.bodyUsed);
    if (response.bodyUsed) {
      console.error('Response body already consumed!');
      return null;
    }
    
    const text = await response.text();
    console.log('Response text length:', text?.length);
    console.log('Response text preview:', text?.substring(0, 200)); // Log first 200 chars
    
    if (!text) {
      console.log('Empty response text');
      return null;
    }
    const parsed = JSON.parse(text);
    console.log('Successfully parsed JSON:', parsed);
    return parsed;
  } catch (error) {
    console.error('JSON parse error:', error);
    return null;
  }
};

export const handleResponse = async (response) => {
  if (response.status === 204) {
    return { status: true, data: null, message: "" };
  }

  const data = await parseJsonSafely(response);

  // If backend returns standard shape {status, data, message}
  if (data && typeof data === "object" && "status" in data) {
    if (
      !data.status &&
      typeof data.message === "string" &&
      data.message === "Token refresh"
    ) {
      if (window.location.pathname !== "/refresh/password") {
        try {
          sessionStorage.setItem(
            "auth.refresh.returnTo",
            `${window.location.pathname}${window.location.search}${window.location.hash}`,
          );
        } catch {
          // ignore
        }
        window.location.href = "/refresh/password";
      }
      return data;
    }

    // Token expired handling (message driven)
    return checkTokenExpired(data);
  }

  return {
    status: response.ok,
    data: data ?? null,
    message: response.ok ? "" : `HTTP error ${response.status}`,
  };
};

export const checkTokenExpired = (data) => {
  if (
    !data.status &&
    typeof data.message === "string" &&
    (data.message === "Token expired" || data.message === "Invalid token")
  ) {
    return handleUnauthorized();
  }
  return data;
};

const handleError = (error) => {
  let message = "Something went wrong, please try again later.";

  if (error?.name === "TypeError" && String(error.message).includes("fetch")) {
    message = "Network error. Please check your connection.";
  } else if (error?.name === "AbortError") {
    message = "Request timeout. Please try again.";
  }

  return {
    status: false,
    message,
    error: error?.message,
  };
};

const createTimeoutController = (timeout = DEFAULT_CONFIG.timeout) => {
  const controller = new AbortController();
  setTimeout(() => controller.abort(), timeout);
  return controller;
};

/** Did the API reject us for a bad/expired token? */
const isAuthFailure = (result) =>
  result &&
  result.status === false &&
  typeof result.message === "string" &&
  ["Token expired", "Invalid token", "Token not found", "Unauthorized"].includes(result.message);

export const apiRequest = async (url, options = {}, isRetry = false) => {
  try {
    const controller = createTimeoutController(options.timeout);

    const config = {
      headers: getAuthHeaders(),
      signal: controller.signal,
      ...options,
      headers: {
        ...getAuthHeaders(),
        ...(options.headers || {}),
      },
    };

    console.log('Making request to:', url, 'with config:', config);
    const response = await fetch(url, config);
    console.log('Raw fetch response:', { url, status: response.status, ok: response.ok });
    const result = await handleResponse(response);
    console.log('Parsed response:', result);

    // Under the Hub the token is a short-lived JWT. When it lapses mid-session
    // the fix is to mint a new one, not to dump the user on a login page the
    // Hub owns — so refresh once and replay the request before giving up.
    if (isAuthFailure(result) && !isRetry) {
      const { hubHosted, refreshHubToken, redirectToHubSignin } = await import("lib/hubSso");
      if (hubHosted()) {
        const fresh = await refreshHubToken();
        if (fresh) return apiRequest(url, options, true);
        redirectToHubSignin();
        return result;
      }
    }
    return result;
  } catch (error) {
    console.error('API Request Error:', error);
    const errorResult = handleError(error);
    console.log('Error result:', errorResult);
    return errorResult;
  }
};

export const apiGet = (url, options = {}) => {
  return apiRequest(url, { method: "GET", ...options });
};

export const apiPost = (url, data = null, options = {}) => {
  return apiRequest(url, {
    method: "POST",
    body: data ? JSON.stringify(data) : null,
    ...options,
  });
};

export const apiPut = (url, data = null, options = {}) => {
  return apiRequest(url, {
    method: "POST",
    body: data ? JSON.stringify(data) : null,
    ...options,
  });
};

export const apiDelete = (url, data = null, options = {}) => {
  return apiRequest(url, {
    method: "DELETE",
    body: data ? JSON.stringify(data) : null,
    ...options,
  });
};

export const apiUpload = (url, formData, options = {}) => {
  const headers = {
    ...getAuthHeaders(),
    ...(options.headers || {}),
  };

  delete headers["Content-Type"];

  return apiRequest(url, {
    method: "POST",
    body: formData,
    headers,
    timeout: 120000, // 2 minutes for file uploads
    ...options,
  });
};

/** The freshest Hub JWT we hold.
 *
 *  localStorage is the slot refreshHubToken() writes, so it is the only source
 *  that is still correct after a mid-session refresh. `store.user.token` is
 *  seeded once by HubGate at boot and — before this — never updated, which is
 *  why calls reading it started failing five minutes in with "Token expired".
 *  Redux is kept as the fallback for the local (non-Hub) login, which sets it
 *  and does not use localStorage.
 */
export const getLiveToken = () => {
  try {
    const t = localStorage.getItem("token");
    if (t) return t;
  } catch {
    /* private mode */
  }
  try {
    // Imported lazily: utils must not pull the store into every bundle.
    // eslint-disable-next-line global-require
    return require("../redux/store").default.getState()?.user?.token || null;
  } catch {
    return null;
  }
};

/** A raw fetch that carries the Hub JWT and survives its expiry.
 *
 *  Returns the parsed body, so each caller keeps its own response mapping.
 *  When the backend says the token lapsed, mint a new one and replay once —
 *  the same recovery apiRequest() already had, now available to the handful of
 *  actions that build their own fetch (multipart uploads, bespoke shapes).
 *  Deliberately does NOT route through handleResponse: its checkTokenExpired()
 *  clears auth and bounces to "/", which under the Hub throws away a session
 *  that only needed a new token.
 */
export const hubFetchJson = async (url, init = {}, isRetry = false) => {
  const token = getLiveToken();
  const res = await fetch(url, {
    ...init,
    headers: { ...(init.headers || {}), ...(token ? { token } : {}) },
  });
  const body = await res.json();

  const lapsed =
    body &&
    body.status === false &&
    typeof body.message === "string" &&
    ["Token expired", "Invalid token"].includes(body.message);

  if (lapsed && !isRetry) {
    const { hubHosted, refreshHubToken } = await import("lib/hubSso");
    if (hubHosted() && (await refreshHubToken())) {
      return hubFetchJson(url, init, true);
    }
  }
  return body;
};

export const ApiUtil = {
  get: apiGet,
  post: apiPost,
  put: apiPut,
  delete: apiDelete,
  upload: apiUpload,
};

export default ApiUtil;
